class AccountingDataValidator {
  // Validate student finance data structure
  validateStudentFinanceData(students) {
    const errors = [];
    const warnings = [];

    students.forEach((student, index) => {
      if (!student.finance || !Array.isArray(student.finance)) {
        warnings.push(`Student ${student.ID || index}: No finance data found`);
        return;
      }

      student.finance.forEach((finance, financeIndex) => {
        // Validate required fields
        if (!finance.totalfees && finance.totalfees !== 0) {
          errors.push(`Student ${student.ID || index}, Finance ${financeIndex}: Missing totalfees`);
        }

        if (!finance.balance && finance.balance !== 0) {
          errors.push(`Student ${student.ID || index}, Finance ${financeIndex}: Missing balance`);
        }

        // Validate payment data
        if (finance.payment && Array.isArray(finance.payment)) {
          finance.payment.forEach((payment, paymentIndex) => {
            if (!payment.amountpaid && payment.amountpaid !== 0) {
              warnings.push(`Student ${student.ID || index}, Payment ${paymentIndex}: Missing amountpaid`);
            }
            if (!payment.des) {
              warnings.push(`Student ${student.ID || index}, Payment ${paymentIndex}: Missing description`);
            }
          });
        }

        // Validate numeric values
        if (finance.totalfees && isNaN(parseFloat(finance.totalfees))) {
          errors.push(`Student ${student.ID || index}, Finance ${financeIndex}: totalfees is not a valid number`);
        }

        if (finance.balance && isNaN(parseFloat(finance.balance))) {
          errors.push(`Student ${student.ID || index}, Finance ${financeIndex}: balance is not a valid number`);
        }
      });
    });

    return { errors, warnings, isValid: errors.length === 0 };
  }

  // Validate expense data structure
  validateExpenseData(expenses) {
    const errors = [];
    const warnings = [];

    expenses.forEach((expense, index) => {
      // Validate required fields
      if (!expense.amountspent && expense.amountspent !== 0) {
        errors.push(`Expense ${index}: Missing amountspent`);
      }

      if (!expense.purpose) {
        warnings.push(`Expense ${index}: Missing purpose`);
      }

      if (!expense.description) {
        warnings.push(`Expense ${index}: Missing description`);
      }

      if (!expense.date) {
        warnings.push(`Expense ${index}: Missing date`);
      }

      // Validate numeric values
      if (expense.amountspent && isNaN(parseFloat(expense.amountspent))) {
        errors.push(`Expense ${index}: amountspent is not a valid number`);
      }

      if (expense.balance && isNaN(parseFloat(expense.balance))) {
        errors.push(`Expense ${index}: balance is not a valid number`);
      }
    });

    return { errors, warnings, isValid: errors.length === 0 };
  }

  // Validate sales data structure
  validateSalesData(sales) {
    const errors = [];
    const warnings = [];

    sales.forEach((sale, index) => {
      // Validate required fields
      if (!sale.total && sale.total !== 0) {
        errors.push(`Sale ${index}: Missing total`);
      }

      if (!sale.paymentMethod) {
        warnings.push(`Sale ${index}: Missing paymentMethod`);
      }

      if (!sale.paymentStatus) {
        warnings.push(`Sale ${index}: Missing paymentStatus`);
      }

      if (!sale.items || !Array.isArray(sale.items) || sale.items.length === 0) {
        errors.push(`Sale ${index}: Missing or empty items array`);
      }

      // Validate numeric values
      if (sale.total && isNaN(parseFloat(sale.total))) {
        errors.push(`Sale ${index}: total is not a valid number`);
      }

      if (sale.subtotal && isNaN(parseFloat(sale.subtotal))) {
        errors.push(`Sale ${index}: subtotal is not a valid number`);
      }

      if (sale.discount && isNaN(parseFloat(sale.discount))) {
        errors.push(`Sale ${index}: discount is not a valid number`);
      }

      if (sale.tax && isNaN(parseFloat(sale.tax))) {
        errors.push(`Sale ${index}: tax is not a valid number`);
      }

      // Validate items
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach((item, itemIndex) => {
          if (!item.quantity || isNaN(parseFloat(item.quantity))) {
            errors.push(`Sale ${index}, Item ${itemIndex}: Invalid quantity`);
          }

          if (!item.unitPrice || isNaN(parseFloat(item.unitPrice))) {
            errors.push(`Sale ${index}, Item ${itemIndex}: Invalid unitPrice`);
          }

          if (!item.totalPrice || isNaN(parseFloat(item.totalPrice))) {
            errors.push(`Sale ${index}, Item ${itemIndex}: Invalid totalPrice`);
          }
        });
      }
    });

    return { errors, warnings, isValid: errors.length === 0 };
  }

  // Validate asset data structure
  validateAssetData(assets) {
    const errors = [];
    const warnings = [];

    assets.forEach((asset, index) => {
      // Validate required fields
      if (!asset.assetName) {
        errors.push(`Asset ${index}: Missing assetName`);
      }

      if (!asset.assetType) {
        errors.push(`Asset ${index}: Missing assetType`);
      }

      if (!asset.assetNumber) {
        errors.push(`Asset ${index}: Missing assetNumber`);
      }

      // Validate numeric values
      if (asset.purchaseCost && isNaN(parseFloat(asset.purchaseCost))) {
        errors.push(`Asset ${index}: purchaseCost is not a valid number`);
      }

      if (asset.currentValue && isNaN(parseFloat(asset.currentValue))) {
        errors.push(`Asset ${index}: currentValue is not a valid number`);
      }

      if (asset.depreciationRate && isNaN(parseFloat(asset.depreciationRate))) {
        errors.push(`Asset ${index}: depreciationRate is not a valid number`);
      }

      // Validate dates
      if (asset.purchaseDate && isNaN(new Date(asset.purchaseDate).getTime())) {
        errors.push(`Asset ${index}: purchaseDate is not a valid date`);
      }

      if (asset.warrantyExpiry && isNaN(new Date(asset.warrantyExpiry).getTime())) {
        errors.push(`Asset ${index}: warrantyExpiry is not a valid date`);
      }
    });

    return { errors, warnings, isValid: errors.length === 0 };
  }

  // Validate teacher data structure
  validateTeacherData(teachers) {
    const errors = [];
    const warnings = [];

    teachers.forEach((teacher, index) => {
      // Validate required fields
      if (!teacher.ID) {
        errors.push(`Teacher ${index}: Missing ID`);
      }

      if (!teacher.lastname) {
        warnings.push(`Teacher ${index}: Missing lastname`);
      }

      if (!teacher.othernames) {
        warnings.push(`Teacher ${index}: Missing othernames`);
      }

      if (!teacher.email) {
        warnings.push(`Teacher ${index}: Missing email`);
      }

      if (!teacher.contact) {
        warnings.push(`Teacher ${index}: Missing contact`);
      }

      // Validate email format
      if (teacher.email && !this.isValidEmail(teacher.email)) {
        errors.push(`Teacher ${index}: Invalid email format`);
      }

      // Validate phone format
      if (teacher.contact && !this.isValidPhone(teacher.contact)) {
        warnings.push(`Teacher ${index}: Invalid phone format`);
      }
    });

    return { errors, warnings, isValid: errors.length === 0 };
  }

  // Validate all financial data
  validateAllFinancialData(reduxData) {
    const results = {
      students: this.validateStudentFinanceData(reduxData.students || []),
      expenses: this.validateExpenseData(reduxData.expenses || []),
      sales: this.validateSalesData(reduxData.sales || []),
      assets: this.validateAssetData(reduxData.assets || []),
      teachers: this.validateTeacherData(reduxData.teachers || [])
    };

    const totalErrors = Object.values(results).reduce((sum, result) => sum + result.errors.length, 0);
    const totalWarnings = Object.values(results).reduce((sum, result) => sum + result.warnings.length, 0);

    return {
      ...results,
      summary: {
        totalErrors,
        totalWarnings,
        isValid: totalErrors === 0,
        validationDate: new Date()
      }
    };
  }

  // Helper methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone);
  }

  // Fix common data issues
  fixCommonDataIssues(reduxData) {
    const fixedData = { ...reduxData };

    // Fix student finance data
    if (fixedData.students) {
      fixedData.students = fixedData.students.map(student => {
        if (student.finance && Array.isArray(student.finance)) {
          student.finance = student.finance.map(finance => ({
            ...finance,
            totalfees: this.fixNumericValue(finance.totalfees),
            balance: this.fixNumericValue(finance.balance),
            Arrears: this.fixNumericValue(finance.Arrears),
            payment: finance.payment ? finance.payment.map(payment => ({
              ...payment,
              amountpaid: this.fixNumericValue(payment.amountpaid)
            })) : []
          }));
        }
        return student;
      });
    }

    // Fix expense data
    if (fixedData.expenses) {
      fixedData.expenses = fixedData.expenses.map(expense => ({
        ...expense,
        amountspent: this.fixNumericValue(expense.amountspent),
        balance: this.fixNumericValue(expense.balance)
      }));
    }

    // Fix sales data
    if (fixedData.sales) {
      fixedData.sales = fixedData.sales.map(sale => ({
        ...sale,
        total: this.fixNumericValue(sale.total),
        subtotal: this.fixNumericValue(sale.subtotal),
        discount: this.fixNumericValue(sale.discount),
        tax: this.fixNumericValue(sale.tax),
        items: sale.items ? sale.items.map(item => ({
          ...item,
          quantity: this.fixNumericValue(item.quantity),
          unitPrice: this.fixNumericValue(item.unitPrice),
          totalPrice: this.fixNumericValue(item.totalPrice)
        })) : []
      }));
    }

    // Fix asset data
    if (fixedData.assets) {
      fixedData.assets = fixedData.assets.map(asset => ({
        ...asset,
        purchaseCost: this.fixNumericValue(asset.purchaseCost),
        currentValue: this.fixNumericValue(asset.currentValue),
        depreciationRate: this.fixNumericValue(asset.depreciationRate)
      }));
    }

    return fixedData;
  }

  // Fix numeric values
  fixNumericValue(value) {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }

  // Generate data quality report
  generateDataQualityReport(reduxData) {
    const validation = this.validateAllFinancialData(reduxData);
    
    return {
      overallScore: this.calculateDataQualityScore(validation),
      recommendations: this.generateRecommendations(validation),
      details: validation,
      generatedAt: new Date()
    };
  }

  // Calculate data quality score (0-100)
  calculateDataQualityScore(validation) {
    const totalFields = this.countTotalFields(validation);
    const errorFields = validation.summary.totalErrors;
    const warningFields = validation.summary.totalWarnings;
    
    const errorPenalty = errorFields * 2; // 2 points per error
    const warningPenalty = warningFields * 0.5; // 0.5 points per warning
    
    const score = Math.max(0, 100 - errorPenalty - warningPenalty);
    return Math.round(score);
  }

  // Count total fields across all data types
  countTotalFields(validation) {
    return Object.values(validation).reduce((sum, result) => {
      if (result && typeof result === 'object' && 'errors' in result) {
        return sum + result.errors.length + result.warnings.length;
      }
      return sum;
    }, 0);
  }

  // Generate recommendations for data improvement
  generateRecommendations(validation) {
    const recommendations = [];

    if (validation.students.errors.length > 0) {
      recommendations.push({
        category: 'Student Finance Data',
        priority: 'High',
        message: 'Fix missing or invalid student finance data to ensure accurate accounting',
        count: validation.students.errors.length
      });
    }

    if (validation.expenses.errors.length > 0) {
      recommendations.push({
        category: 'Expense Data',
        priority: 'High',
        message: 'Fix missing or invalid expense data for proper cost tracking',
        count: validation.expenses.errors.length
      });
    }

    if (validation.sales.errors.length > 0) {
      recommendations.push({
        category: 'Sales Data',
        priority: 'High',
        message: 'Fix missing or invalid sales data for revenue tracking',
        count: validation.sales.errors.length
      });
    }

    if (validation.assets.errors.length > 0) {
      recommendations.push({
        category: 'Asset Data',
        priority: 'Medium',
        message: 'Fix missing or invalid asset data for proper asset management',
        count: validation.assets.errors.length
      });
    }

    if (validation.teachers.warnings.length > 0) {
      recommendations.push({
        category: 'Teacher Data',
        priority: 'Low',
        message: 'Complete missing teacher information for better record keeping',
        count: validation.teachers.warnings.length
      });
    }

    return recommendations;
  }
}

export default new AccountingDataValidator();
